import {setDifference} from '@benchling/js-utils';

export type Variable = string;
/** An equation solving for a single variable in terms of other variables. */
export interface Equation<TVar extends Variable, TValue = number> {
  /** What variable you are solving for */
  solution: TVar;
  /** What variables are required to solve for the solution */
  deps: Array<TVar>;
  /** How to get the solution from the provided dependencies.
   * You should only assume that the dependencies in the dependency array are provided. (Enforcing this with TypeScript is possible, but in turn hurts the DevX.)
   * */
  calc: (varValues: Record<TVar, TValue>) => TValue;
}

export type FlatEquationSolverCanSolveResult<TVar extends Variable> = false | {usedDeps: Set<TVar>};

export type FlatEquationSolverResult<TVar extends Variable, TValue = number> =
  | {solution: TValue; equation: Equation<TVar, TValue>}
  | {missingDeps: Set<TVar>};

interface OptimizedLookupEquation<TVar extends Variable, TValue = number> {
  deps: Set<TVar>;
  calc: (varValues: Record<TVar, TValue>) => TValue;
  equation: Equation<TVar, TValue>;
}

type EquationLookup<TVar extends Variable, TValue = number> = Partial<
  Record<TVar, Array<OptimizedLookupEquation<TVar, TValue>>>
>;

/**
 * A solver for a set of equations that can be solved in a single pass. Notably this does not support recursively solving equations, nor is it aware of the rules of algebra.
 *
 * Provide a set of equations and the solver will use what's available to it to either provide the solution or return the missing dependencies.
 */
class FlatEquationSolver<TVar extends Variable, TValue = number> {
  private equationLookup: EquationLookup<TVar, TValue>;

  constructor(equations: Array<Equation<TVar, TValue>>) {
    this.equationLookup = equations.reduce<EquationLookup<TVar, TValue>>((acc, equation) => {
      const {solution, deps, calc} = equation;
      if (!acc[solution]) {
        acc[solution] = [];
      }
      acc[solution].push({
        deps: new Set(deps),
        calc,
        equation,
      });
      return acc;
    }, {});
  }

  /** Returns true if the solver has an equation that can be fully solved given the provided variable values. */
  canSolve(
    solveFor: TVar,
    availableVars: Partial<Record<TVar, boolean>>
  ): FlatEquationSolverCanSolveResult<TVar> {
    const equations = this.equationLookup[solveFor];
    if (!equations) {
      return false;
    }
    const foundEquation = equations.find(
      (equation) =>
        setDifference(
          equation.deps,
          new Set(
            Object.entries(availableVars)
              .filter(([_, val]) => Boolean(val))
              .map(([key]) => key)
          )
        ).size === 0
    );
    if (foundEquation) {
      return {usedDeps: foundEquation.deps};
    }
    return false;
  }

  /** Solve for a given variable using the provided equations.
   *
   * Can return either the solution or a list of the missing dependencies.
   */
  solve(solveFor: TVar, varValues: Partial<Record<TVar, TValue>>): FlatEquationSolverResult<TVar, TValue> {
    let allMissingDeps = new Set<TVar>();
    const eqs = this.equationLookup[solveFor];
    if (!eqs) {
      throw new Error(`No equations found to solve for ${solveFor}`);
    }
    for (const eq of eqs) {
      const {deps, calc, equation} = eq;
      const missingDeps = setDifference(
        deps,
        new Set(
          Object.entries(varValues)
            .filter(([_, val]) => val !== undefined)
            .map(([key]) => key as TVar)
        )
      );
      if (missingDeps.size === 0) {
        return {solution: calc(varValues as Record<TVar, TValue>), equation};
      }
      allMissingDeps = new Set([...allMissingDeps, ...missingDeps]);
    }
    return {missingDeps: allMissingDeps};
  }
}

export default FlatEquationSolver;
